import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { ToastProvider } from './components/Toast';
import { logActivity, profileApi, vendorProfileApi } from './api/client';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Opportunities from './pages/Opportunities';
import OpportunityDetail from './pages/OpportunityDetail';
import Vendors from './pages/Vendors';
import VendorDetail from './pages/VendorDetail';
import Awards from './pages/Awards';
import Engagement from './pages/Engagement';
import Alerts from './pages/Alerts';
import Cases from './pages/Cases';
import TasksApprovals from './pages/TasksApprovals';
import Support from './pages/Support';
import Training from './pages/Training';
import SamLibrary from './pages/SamLibrary';
import PriceBenchmarkExplorer from './pages/PriceBenchmarkExplorer';
import AIGovernance from './pages/AIGovernance';
import SecurityReadiness from './pages/SecurityReadiness';
import UserManagement from './pages/UserManagement';
import ActivityLog from './pages/ActivityLog';
import ModelTraining from './pages/ModelTraining';
import Discovery from './pages/Discovery';
import MatchMe from './pages/MatchMe';
import SellerOnboarding from './pages/SellerOnboarding';
import AwsUsage from './pages/AwsUsage';
import BidNoBid from './pages/BidNoBid';
import Teaming from './pages/Teaming';

const ADMIN_EMAILS = ['jordan@firstoinvest.com'];

// Fires a single 'login' activity event when a user session becomes active.
// Uses sessionStorage so it logs once per browser session (not on every re-render).
function LoginLogger({ user }: { user: any }) {
  React.useEffect(() => {
    const uid = user?.userId || user?.username || '';
    if (!uid) return;
    const key = `fti_login_logged_${uid}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      logActivity('login', { detail: 'User signed in' });
    }
  }, [user]);
  return null;
}

// Emits a 'heartbeat' activity event once per minute WHILE the tab is visible and
// the user has been active in the last few minutes. These pings let the backend
// reconstruct real time-in-system (active use), not idle tab time.
function PresenceTracker({ user }: { user: any }) {
  React.useEffect(() => {
    const uid = user?.userId || user?.username || '';
    if (!uid) return;

    const IDLE_MS = 3 * 60 * 1000;   // consider idle after 3 min of no input
    const TICK_MS = 60 * 1000;       // heartbeat cadence
    let lastActive = Date.now();
    const bump = () => { lastActive = Date.now(); };

    const opts: AddEventListenerOptions = { passive: true };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, bump, opts));

    // Fire one immediately so a short visit still registers presence.
    logActivity('heartbeat');

    const timer = window.setInterval(() => {
      const active = (Date.now() - lastActive) < IDLE_MS;
      if (document.visibilityState === 'visible' && active) {
        logActivity('heartbeat');
      }
    }, TICK_MS);

    return () => {
      window.clearInterval(timer);
      events.forEach(e => window.removeEventListener(e, bump, opts));
    };
  }, [user]);
  return null;
}

// First-run gate for SELLER-lens users: a seller with no saved capability profile is
// redirected to /seller-onboarding until they build one. Buyers are never affected.
// Checks the server once per mount; caches the "done" result so it doesn't re-check.
function SellerGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    let cancelled = false;
    // Cheap short-circuit: if we've already confirmed a profile this session, skip.
    if (sessionStorage.getItem('fti_seller_profile_ok') === '1') return;
    (async () => {
      try {
        const prof = await profileApi.get();
        const isSeller = prof?.profile?.org_type === 'seller';
        if (!isSeller) { sessionStorage.setItem('fti_seller_profile_ok', '1'); return; }
        const vp = await vendorProfileApi.get();
        const cp = vp?.capability_profile;
        const hasProfile = cp && Object.keys(cp).length > 0;
        if (hasProfile) {
          sessionStorage.setItem('fti_seller_profile_ok', '1');
        } else if (!cancelled && location.pathname !== '/seller-onboarding') {
          navigate('/seller-onboarding', { replace: true });
        }
      } catch { /* on error, don't block the user */ }
    })();
    return () => { cancelled = true; };
    // Re-run when the path changes so a seller who navigates away pre-completion is nudged back.
  }, [location.pathname, navigate]);
  return null;
}

export default function App() {
  return (
    <Authenticator
      loginMechanisms={['email']}
      variation="modal"
      components={{
        Header: () => (
          <div style={{ textAlign: 'center', padding: '24px 0 8px', background: '#1a2744' }}>
            <h1 style={{ color: 'white', fontSize: '20px', fontWeight: 700 }}>FTI Procurement Intelligence</h1>
            <p style={{ color: '#93c5fd', fontSize: '13px', marginTop: 4 }}>First to Invest &mdash; Market Research Platform</p>
          </div>
        ),
      }}
    >
      {({ signOut, user }) => {
        const userEmail = ((user as any)?.signInDetails?.loginId || user?.username || '').toLowerCase();
        const isAdmin = ADMIN_EMAILS.includes(userEmail);
        return (
        <ToastProvider>
        <LoginLogger user={user} />
        <PresenceTracker user={user} />
        <BrowserRouter>
          <SellerGuard />
          <Layout user={user} signOut={signOut!}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/seller-onboarding" element={<SellerOnboarding onComplete={() => sessionStorage.setItem('fti_seller_profile_ok', '1')} />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/opportunities" element={<Opportunities />} />
              <Route path="/opportunities/:id" element={<OpportunityDetail />} />
              <Route path="/sam-library" element={<SamLibrary />} />
              {/* Buyside (seller) lens pages */}
              <Route path="/discovery" element={<Discovery />} />
              <Route path="/match-me" element={<MatchMe />} />
              <Route path="/bid-no-bid" element={<BidNoBid />} />
              <Route path="/teaming" element={<Teaming />} />
              <Route path="/price-benchmark" element={<PriceBenchmarkExplorer />} />
              <Route path="/vendors" element={<Vendors />} />
              <Route path="/vendors/:id" element={<VendorDetail />} />
              <Route path="/cases" element={<Cases />} />
              <Route path="/engagement" element={<Engagement />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/tasks" element={<TasksApprovals />} />
              <Route path="/ai-governance" element={isAdmin ? <AIGovernance /> : <Navigate to="/dashboard" replace />} />
              <Route path="/security-readiness" element={isAdmin ? <SecurityReadiness /> : <Navigate to="/dashboard" replace />} />
              <Route path="/admin/users" element={isAdmin ? <UserManagement /> : <Navigate to="/dashboard" replace />} />
              <Route path="/admin/activity" element={isAdmin ? <ActivityLog /> : <Navigate to="/dashboard" replace />} />
              <Route path="/admin/model-training" element={isAdmin ? <ModelTraining /> : <Navigate to="/dashboard" replace />} />
              <Route path="/admin/aws-usage" element={isAdmin ? <AwsUsage /> : <Navigate to="/dashboard" replace />} />
              <Route path="/support" element={<Support />} />
              <Route path="/training" element={<Training />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        </BrowserRouter>
        </ToastProvider>
        );
      }}
    </Authenticator>
  );
}
